import { Helmet } from 'react-helmet-async';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useToast } from '../../hooks/useToast';
import fTitle from '../../utils/owner-portal/formatTitle';
import { VerifyCredentialForm } from '../../sections/auth/VerifyCredentialForm';
import LoginLayout from '../../layouts/LoginLayout';
import { PATH_DEV } from '../../routes/paths';
import LoginFlowProvider, {
  SignInStep,
  SignInMethod,
  useLoginFlow
} from '../../auth/LoginProvider';
import EmailSentConfirmation from '../../components/login/EmailSentConfirmation';
import SelectVerificationMethod from '../../components/login/SelectVerificationMethod';
import ConfirmCode from '../../components/login/ConfirmCode';
import AuthLoginForm from '../../sections/auth/AuthLoginForm';
import { CheckEnvironment, SupportedEnvironments } from '../../config-global';
import { clientVersion } from '../../utils/owner-portal/version';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';

// ----------------------------------------------------------------------

export default function OobSignInPage(
  { emailOnly }: { emailOnly?: boolean } = { emailOnly: false }
) {
  return (
    <>
      <Helmet>
        <title>{fTitle('Login')}</title>
      </Helmet>
      <div id="hidden-recaptcha-element" />
      <LoginFlowProvider>
        <LoginWithEmail emailOnly={emailOnly} />
      </LoginFlowProvider>
    </>
  );
}

// ----------------------------------------------------------------------

function LoginWithEmail({ emailOnly: _emailOnly }: { emailOnly?: boolean } = { emailOnly: false }) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { toast } = useToast();

  const { setAlertFromOOBAction, signInStep, setSignInStep, setConfirmationResult, signInMethod } =
    useLoginFlow();

  useEffect(() => {
    if (state) {
      if (state.error) {
        toast({ title: state.error, variant: 'destructive' });
      }
      if (state.alert) {
        setAlertFromOOBAction(state.alert);
      }
    }
  }, [state, toast, setAlertFromOOBAction]);

  const devToolsCard =
    CheckEnvironment(SupportedEnvironments.Dev) && signInStep === SignInStep.Initial ? (
      <Card className="w-full max-w-md shadow-lg">
        <CardContent className="pt-4 pb-4 text-foreground">
          <h2 className="text-sm font-semibold text-foreground mb-3">Developer tools</h2>
          <div className="flex flex-col gap-2">
            <Button
              variant="warning"
              onClick={() => {
                setSignInStep('Developer Login');
              }}
            >
              Developer Login
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                navigate(PATH_DEV.brand);
              }}
            >
              Brand Selector
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-3 tabular-nums">
            {`v${clientVersion} (dev)`}
          </p>
        </CardContent>
      </Card>
    ) : null;

  return (
    <LoginLayout belowCard={devToolsCard}>
      {signInStep === SignInStep.Initial && (
        <VerifyCredentialForm
          next={(method: string) => {
            if (method === 'email') {
              setSignInStep(SignInStep.Confirm);
            } else if (method === 'phone') {
              setSignInStep(SignInStep.SelectMethod);
            }
          }}
        />
      )}
      {signInStep === SignInStep.SelectMethod && (
        <SelectVerificationMethod
          next={(confirmResult) => {
            setSignInStep(SignInStep.Confirm);
            setConfirmationResult(confirmResult);
          }}
          back={() => {
            setSignInStep(SignInStep.Initial);
          }}
        />
      )}
      {signInStep === SignInStep.Confirm && signInMethod === SignInMethod.Email && (
        <EmailSentConfirmation
          back={() => {
            setSignInStep(SignInStep.Initial);
          }}
        />
      )}
      {signInStep === SignInStep.Confirm && signInMethod === SignInMethod.Phone && (
        <ConfirmCode
          back={() => {
            setSignInStep(SignInStep.Initial);
          }}
        />
      )}

      {signInStep === 'Developer Login' && (
        <AuthLoginForm
          back={() => {
            setSignInStep(SignInStep.Initial);
          }}
        />
      )}
    </LoginLayout>
  );
}
